import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FREQUENCY_OPTIONS,
  CALENDAR_FREQUENCIES,
  INTERVAL_UNIT_LABELS,
  WEEKDAY_LABELS,
  NTH_VALUES,
  NTH_SELECT_LABELS,
} from '../shared/utils/recurrence/constants';
import {
  INTERVAL_FREQUENCIES,
  MIN_INTERVAL,
  MAX_INTERVAL,
  DEFAULT_INTERVAL,
  canonicalizeRepeatRule,
} from '../shared/utils/recurrence/canonicalize';
import {
  WEEKDAY_PICKER_LETTERS,
  parseWeekdaysArray,
  deriveWeekdaysFromAnchor,
} from '../shared/utils/recurrence/weekdays';
import {
  buildRepeatRuleForFrequency,
  normalizeRepeatRule,
} from '../shared/utils/recurrence/ruleHelpers';
import { describeRepeatRule } from '../shared/utils/recurrence/describeRule';
import './RepeatPatternPicker.css';

/** Above modals (1100) and most overlays; matches TimeSelect / SearchableSelect. */
const REPEAT_PICKER_POPOVER_Z_INDEX = 11000;

/** Popover width — fits monthly pattern rows on one line without excess empty space. */
const REPEAT_PICKER_PANEL_MIN_WIDTH = 320;

function getIntervalUnitLabel(frequency, interval) {
  const pair = INTERVAL_UNIT_LABELS[frequency];
  if (!pair) return '';
  return interval === 1 ? pair[0] : pair[1];
}

/**
 * Editable recurrence fields (used inside popover panel).
 */
export function RepeatPatternPickerPanel({
  anchorDate,
  rule,
  onRuleChange,
  disabled = false,
  idPrefix = 'repeat-picker',
}) {
  const anchor = (anchorDate || '').trim() || null;
  const frequency = !rule || !rule.frequency ? 'never' : rule.frequency;
  const showInterval = INTERVAL_FREQUENCIES.includes(frequency);
  const showWeeklyDays = frequency === 'weekly';
  const showCalendarSub = CALENDAR_FREQUENCIES.includes(frequency);
  const selectedWeekdays =
    rule && showWeeklyDays
      ? parseWeekdaysArray(rule.weekdays) || deriveWeekdaysFromAnchor(anchor)
      : [];
  const interval =
    rule && Number.isInteger(rule.interval) && rule.interval >= MIN_INTERVAL
      ? rule.interval
      : DEFAULT_INTERVAL;

  const baseSelectProps = {
    disabled,
    className: undefined,
  };

  function handleFrequencyChange(nextFreq) {
    onRuleChange(buildRepeatRuleForFrequency(nextFreq, anchor, rule));
  }

  function handleIntervalChange(raw) {
    if (!rule || !showInterval) return;
    const n = Math.min(MAX_INTERVAL, Math.max(MIN_INTERVAL, Number(raw) || MIN_INTERVAL));
    onRuleChange(canonicalizeRepeatRule({ ...rule, interval: n }));
  }

  function toggleWeekday(day) {
    if (!rule || !showWeeklyDays) return;
    const current = parseWeekdaysArray(rule.weekdays) || deriveWeekdaysFromAnchor(anchor);
    let next;
    if (current.includes(day)) {
      if (current.length <= 1) return;
      next = current.filter((d) => d !== day);
    } else {
      next = [...current, day].sort((a, b) => a - b);
    }
    onRuleChange(canonicalizeRepeatRule({ ...rule, weekdays: next }));
  }

  function setMode(mode) {
    if (!rule || !CALENDAR_FREQUENCIES.includes(rule.frequency)) return;
    const derived = buildRepeatRuleForFrequency(rule.frequency, anchor, { ...rule, mode });
    onRuleChange(derived);
  }

  const calendarHeading =
    frequency === 'yearly' ? 'Yearly pattern' : 'Monthly pattern';

  return (
    <div className="repeat-picker-panel-fields">
      <div className="repeat-picker-field">
        <label className="repeat-picker-field-label" htmlFor={`${idPrefix}-frequency`}>
          Frequency
        </label>
        <select
          id={`${idPrefix}-frequency`}
          value={frequency}
          onChange={(e) => handleFrequencyChange(e.target.value)}
          {...baseSelectProps}
        >
          {FREQUENCY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {showInterval && rule && (
        <div className="repeat-picker-field repeat-picker-interval-row">
          <span className="repeat-picker-field-label">Repeat every</span>
          <span className="repeat-picker-interval-inner">
            <input
              id={`${idPrefix}-interval`}
              type="number"
              min={MIN_INTERVAL}
              max={MAX_INTERVAL}
              className="repeat-picker-interval-input input-number-no-spin"
              value={interval}
              onChange={(e) => handleIntervalChange(e.target.value)}
              disabled={disabled}
              aria-label="Repeat interval"
            />
            <span className="repeat-picker-interval-unit" aria-hidden="true">
              {getIntervalUnitLabel(frequency, interval)}
            </span>
          </span>
        </div>
      )}

      {showWeeklyDays && rule && (
        <div className="repeat-picker-field repeat-picker-weekdays">
          <span className="repeat-picker-field-label" id={`${idPrefix}-weekdays-label`}>
            Repeat on
          </span>
          <div
            className="repeat-picker-weekday-row"
            role="group"
            aria-labelledby={`${idPrefix}-weekdays-label`}
          >
            {WEEKDAY_PICKER_LETTERS.map((letter, day) => {
              const selected = selectedWeekdays.includes(day);
              const dayNames = [
                'Sunday',
                'Monday',
                'Tuesday',
                'Wednesday',
                'Thursday',
                'Friday',
                'Saturday',
              ];
              return (
                <button
                  key={day}
                  type="button"
                  className={`repeat-picker-weekday-btn${selected ? ' repeat-picker-weekday-btn--selected' : ''}`}
                  aria-pressed={selected}
                  aria-label={dayNames[day]}
                  disabled={disabled}
                  onClick={() => toggleWeekday(day)}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {showCalendarSub && rule && (
        <div className="repeat-picker-pattern">
          <div className="repeat-picker-pattern-heading">{calendarHeading}</div>

          <label className="repeat-picker-pattern-row">
            <input
              type="radio"
              name={`${idPrefix}-mode`}
              checked={rule.mode === 'dayOfMonth'}
              onChange={() => setMode('dayOfMonth')}
              disabled={disabled}
            />
            <span className="repeat-picker-pattern-row-inner">
              <span className="repeat-picker-pattern-text">On day</span>
              <input
                id={`${idPrefix}-dom`}
                type="number"
                min={1}
                max={31}
                className="repeat-picker-day-input input-number-no-spin"
                value={rule.dayOfMonth != null ? rule.dayOfMonth : 1}
                onChange={(e) => {
                  const dom = Math.min(31, Math.max(1, Number(e.target.value) || 1));
                  onRuleChange({ ...rule, mode: 'dayOfMonth', dayOfMonth: dom });
                }}
                onFocus={() => {
                  if (rule.mode !== 'dayOfMonth') setMode('dayOfMonth');
                }}
                disabled={disabled}
                aria-label="Day of month"
              />
              <span className="repeat-picker-pattern-text">of month</span>
            </span>
          </label>

          <label className="repeat-picker-pattern-row">
            <input
              type="radio"
              name={`${idPrefix}-mode`}
              checked={rule.mode === 'nthWeekday'}
              onChange={() => setMode('nthWeekday')}
              disabled={disabled}
            />
            <span className="repeat-picker-pattern-row-inner">
              <span className="repeat-picker-pattern-text">On the</span>
              <select
                className="repeat-picker-inline-select"
                aria-label="Week of month"
                value={rule.nth === 'last' ? 'last' : String(rule.nth)}
                onChange={(e) => {
                  const nth = e.target.value === 'last' ? 'last' : Number(e.target.value);
                  onRuleChange({ ...rule, mode: 'nthWeekday', nth });
                }}
                onFocus={() => {
                  if (rule.mode !== 'nthWeekday') setMode('nthWeekday');
                }}
                disabled={disabled}
              >
                {NTH_VALUES.map((n) => (
                  <option key={String(n)} value={String(n)}>
                    {NTH_SELECT_LABELS[n]}
                  </option>
                ))}
              </select>
              <select
                className="repeat-picker-inline-select"
                aria-label="Day of week"
                value={String(rule.weekday)}
                onChange={(e) =>
                  onRuleChange({ ...rule, mode: 'nthWeekday', weekday: Number(e.target.value) })
                }
                onFocus={() => {
                  if (rule.mode !== 'nthWeekday') setMode('nthWeekday');
                }}
                disabled={disabled}
              >
                {WEEKDAY_LABELS.map((label, idx) => (
                  <option key={label} value={String(idx)}>
                    {label}
                  </option>
                ))}
              </select>
              <span className="repeat-picker-pattern-text">of month</span>
            </span>
          </label>
        </div>
      )}
    </div>
  );
}

/**
 * Reusable recurrence picker: compact trigger + popover panel (like TimeSelect / date picker).
 */
export default function RepeatPatternPicker({
  id,
  anchorDate,
  value,
  onChange,
  selectStyle = {},
  className = '',
  disabled = false,
  autoFocus = false,
  onBlur,
  /** Render menu in document.body portal (recommended in modals). */
  dropdownPortal = true,
  /** Inline form row: fixed-width trigger, does not grow and steal space from neighbors. */
  compact = false,
}) {
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const menuPortalRef = useRef(null);
  const onBlurRef = useRef(onBlur);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [menuFixed, setMenuFixed] = useState(null);

  const anchor = (anchorDate || '').trim() || null;
  const displayLabel = describeRepeatRule(normalizeRepeatRule(value, anchor));

  useEffect(() => {
    onBlurRef.current = onBlur;
  }, [onBlur]);

  /** Defer blur so parent state from onChange and latest onBlur handler run before autosave. */
  const scheduleFieldCommit = useCallback(() => {
    window.setTimeout(() => onBlurRef.current?.(), 0);
  }, []);

  useEffect(() => {
    if (!open) return;
    setDraft(normalizeRepeatRule(value, anchor));
  }, [open, value, anchor]);

  useEffect(() => {
    if (!open) return;
    const closeOnOutside = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      if (dropdownPortal && menuPortalRef.current?.contains(e.target)) return;
      setOpen(false);
      scheduleFieldCommit();
    };
    const closeOnEsc = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        scheduleFieldCommit();
      }
    };
    document.addEventListener('mousedown', closeOnOutside);
    window.addEventListener('keydown', closeOnEsc);
    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      window.removeEventListener('keydown', closeOnEsc);
    };
  }, [open, dropdownPortal, scheduleFieldCommit]);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) {
      if (!open) setMenuFixed(null);
      return;
    }
    const update = () => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const panelWidth = REPEAT_PICKER_PANEL_MIN_WIDTH;
      const panelHeight = 340;
      const preferredTop = rect.bottom + 4;
      const clampedTop = Math.min(preferredTop, window.innerHeight - panelHeight - 8);
      const top = Math.max(8, clampedTop);
      let left = rect.left;
      if (left + panelWidth > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - panelWidth - 8);
      }
      setMenuFixed({ left, top, width: panelWidth });
    };
    update();
    if (dropdownPortal) {
      window.addEventListener('scroll', update, true);
      window.addEventListener('resize', update);
      return () => {
        window.removeEventListener('scroll', update, true);
        window.removeEventListener('resize', update);
      };
    }
    return undefined;
  }, [open, dropdownPortal]);

  function handleCancel() {
    setOpen(false);
    scheduleFieldCommit();
  }

  function handleDone() {
    if (onChange) onChange(draft);
    setOpen(false);
    scheduleFieldCommit();
  }

  const triggerStyle = {
    width: '100%',
    padding: '6px 8px',
    fontSize: '13px',
    borderRadius: '4px',
    border: '1px solid #ced4da',
    boxSizing: 'border-box',
    ...selectStyle,
  };

  const panelContent = (
    <div
      className="repeat-picker-panel"
      role="dialog"
      aria-label="Repeat schedule"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="repeat-picker-panel-body">
        <RepeatPatternPickerPanel
          anchorDate={anchor}
          rule={draft}
          onRuleChange={setDraft}
          disabled={disabled}
          idPrefix={id || 'repeat-picker'}
        />
      </div>
      <div className="repeat-picker-actions">
        <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={disabled}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" onClick={handleDone} disabled={disabled}>
          Done
        </button>
      </div>
    </div>
  );

  const wrapperStyle = compact
    ? {
        flex: '1 1 auto',
        minWidth: selectStyle.minWidth ?? 168,
        maxWidth: selectStyle.maxWidth ?? '100%',
        width: selectStyle.width ?? '100%',
      }
    : { width: selectStyle.width || '100%' };

  return (
    <div ref={rootRef} className={`repeat-picker ${compact ? 'repeat-picker--compact' : ''} ${className}`.trim()} style={wrapperStyle}>
      <button
        id={id}
        ref={buttonRef}
        type="button"
        className="repeat-picker-trigger"
        disabled={disabled}
        autoFocus={autoFocus}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={displayLabel}
        onClick={() => {
          if (disabled) return;
          setOpen((prev) => !prev);
        }}
        style={triggerStyle}
      >
        <span className="repeat-picker-trigger-label">{displayLabel}</span>
        <span aria-hidden="true" style={{ opacity: 0.7, flexShrink: 0 }}>
          ▾
        </span>
      </button>

      {open && !dropdownPortal && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 'calc(100% + 4px)',
            zIndex: REPEAT_PICKER_POPOVER_Z_INDEX,
            width: '100%',
            minWidth: REPEAT_PICKER_PANEL_MIN_WIDTH,
          }}
        >
          {panelContent}
        </div>
      )}

      {open &&
        dropdownPortal &&
        menuFixed != null &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={menuPortalRef}
            style={{
              position: 'fixed',
              left: menuFixed.left,
              top: menuFixed.top,
              width: menuFixed.width,
              zIndex: REPEAT_PICKER_POPOVER_Z_INDEX,
            }}
          >
            {panelContent}
          </div>,
          document.body
        )}
    </div>
  );
}
